// src/pages/dashboard/AdminProducts.jsx
// Gestion admin des produits — Liste + CRUD (créer, modifier, supprimer).

import { useState, useEffect, useRef } from 'react'
import {
  Plus, Search, Edit3, Trash2, Loader2, Package, Star, Eye, EyeOff,
  X, Save, Check, AlertTriangle, Image as ImageIcon, Upload,
} from 'lucide-react'
import api from '../../lib/axios'

/* ---------- Design tokens (identiques à la Home) ---------- */
const card = 'bg-white rounded-2xl shadow-[0_4px_24px_rgba(15,23,42,0.06)]'
const press = 'transition-all duration-200 ease-out hover:scale-105 active:scale-95 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#006b5f]/30'

const CATEGORIES = [
  { id: 'google_review', label: 'Avis Google' },
  { id: 'menu', label: 'Menu' },
  { id: 'nfc_card', label: 'Carte NFC' },
  { id: 'loyalty', label: 'Fidélité' },
  { id: 'pack', label: 'Pack' },
  { id: 'other', label: 'Autre' },
]

const fmt = (n, currency = 'MAD') => {
  const symbol = currency === 'MAD' ? 'DH' : currency === 'EUR' ? '€' : currency
  return Number(n || 0).toFixed(0) + ' ' + symbol
}

/* ---------- Modal générique ---------- */
function Modal({ open, onClose, title, children, size = 'md' }) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null
  const maxW = size === 'lg' ? 'max-w-2xl' : size === 'sm' ? 'max-w-sm' : 'max-w-lg'

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        onClick={onClose}
        className="absolute inset-0 bg-[#000a1e]/60 backdrop-blur-sm"
      />
      <div
        className={`relative w-full ${maxW} max-h-[92vh] overflow-y-auto bg-white rounded-3xl shadow-2xl p-7`}
      >
        <button
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-[#eff2ff] flex items-center justify-center hover:scale-110 hover:rotate-90 active:scale-95 transition-all"
        >
          <X className="w-4 h-4 text-[#000a1e]" />
        </button>
        {title && (
          <h3 className="font-bold text-xl text-[#000a1e] pr-10 mb-5">{title}</h3>
        )}
        {children}
      </div>
    </div>
  )
}

/* ---------- Champ de formulaire ---------- */
function Field({ label, error, hint, ...props }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#000a1e]">{label}</span>
      {hint && <span className="text-[11px] text-slate-400 ml-1">{hint}</span>}
      <input
        {...props}
        className={`mt-1.5 w-full px-3.5 py-2.5 rounded-xl bg-[#f9f9ff] border text-sm text-[#000a1e] focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] ${
          error ? 'border-red-400' : 'border-slate-200'
        }`}
      />
      {error && <span className="text-[11px] text-red-500 mt-1 block">{error}</span>}
    </label>
  )
}

/* ---------- Formulaire produit (création + édition) ---------- */
function ProductForm({ product, onSave, onCancel }) {
  const isEdit = !!product?.id
  const [form, setForm] = useState({
    name: product?.name || '',
    slug: product?.slug || '',
    short_description: product?.short_description || '',
    description: product?.description || '',
    price: product?.price ?? '',
    currency: product?.currency || 'MAD',
    image: product?.image || '',
    category: product?.category || 'nfc_card',
    features: (product?.features || []).join('\n'),
    is_active: product?.is_active ?? true,
    is_featured: product?.is_featured ?? false,
    position: product?.position ?? 0,
  })
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }))

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('type', 'product')
      const { data } = await api.post('/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setForm((f) => ({ ...f, image: data.url }))
    } catch (err) {
      console.error('Erreur upload:', err)
      setErrors((e) => ({ ...e, image: 'Erreur upload image.' }))
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  const submit = async (e) => {
    e.preventDefault()
    const err = {}
    if (form.name.trim().length < 2) err.name = 'Indiquez un nom.'
    if (form.price === '' || Number(form.price) < 0) err.price = 'Prix invalide.'
    setErrors(err)
    if (Object.keys(err).length) return

    setSaving(true)
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        position: Number(form.position) || 0,
        features: form.features
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
      }
      await onSave(payload, isEdit ? product.id : null)
    } catch (err) {
      const data = err.response?.data
      const msg =
        data?.message ||
        (data?.errors ? Object.values(data.errors).flat().join(' • ') : '') ||
        'Erreur lors de la sauvegarde.'
      setErrors({ _global: msg })
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      {/* Image */}
      <div>
        <label className="block text-xs font-semibold text-[#000a1e] mb-1.5">
          Image du produit
        </label>
        <div className="flex items-center gap-3">
          <div className="w-24 h-24 rounded-xl bg-[#eff2ff] overflow-hidden flex items-center justify-center shrink-0">
            {form.image ? (
              <img src={form.image} alt="" className="w-full h-full object-cover" />
            ) : (
              <ImageIcon className="w-8 h-8 text-[#006b5f]/40" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className={`${press} inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#eff2ff] text-[#000a1e] text-xs font-semibold`}
            >
              {uploading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Upload className="w-3.5 h-3.5" />
              )}
              {uploading ? 'Upload...' : 'Téléverser une image'}
            </button>
            <input
              type="text"
              value={form.image}
              onChange={set('image')}
              placeholder="ou collez une URL https://..."
              className="w-full px-3 py-2 rounded-lg bg-[#f9f9ff] border border-slate-200 text-xs focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f]"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field
          label="Nom du produit"
          value={form.name}
          onChange={set('name')}
          error={errors.name}
          placeholder="Carte NFC Google Review"
        />
        <Field
          label="Slug"
          hint="(auto si vide)"
          value={form.slug}
          onChange={set('slug')}
          placeholder="carte-nfc-google-review"
        />
      </div>

      <Field
        label="Description courte"
        value={form.short_description}
        onChange={set('short_description')}
        placeholder="Obtenez plus d'avis Google en un tap."
      />

      <div>
        <label className="block text-xs font-semibold text-[#000a1e] mb-1.5">
          Description complète
        </label>
        <textarea
          rows={3}
          value={form.description}
          onChange={set('description')}
          placeholder="Description longue du produit..."
          className="w-full px-3.5 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm text-[#000a1e] focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] resize-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="Prix"
          type="number"
          step="0.01"
          min="0"
          value={form.price}
          onChange={set('price')}
          error={errors.price}
          placeholder="149"
        />
        <div>
          <label className="block text-xs font-semibold text-[#000a1e]">Devise</label>
          <select
            value={form.currency}
            onChange={set('currency')}
            className="mt-1.5 w-full px-3.5 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f]"
          >
            <option value="MAD">MAD (DH)</option>
            <option value="EUR">EUR (€)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-[#000a1e] mb-1.5">
          Catégorie
        </label>
        <select
          value={form.category}
          onChange={set('category')}
          className="w-full px-3.5 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f]"
        >
          {CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-semibold text-[#000a1e] mb-1.5">
          Caractéristiques{' '}
          <span className="text-slate-400 font-normal">(une par ligne)</span>
        </label>
        <textarea
          rows={4}
          value={form.features}
          onChange={set('features')}
          placeholder={'NFC NTAG 213\nQR Code au dos\nImpression premium'}
          className="w-full px-3.5 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f] resize-none font-mono"
        />
      </div>

      <div className="flex flex-wrap gap-5 pt-1">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={setBool('is_active')}
            className="w-4 h-4 accent-[#006b5f]"
          />
          <span className="text-sm text-[#000a1e] font-semibold">Produit actif</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={setBool('is_featured')}
            className="w-4 h-4 accent-[#006b5f]"
          />
          <span className="text-sm text-[#000a1e] font-semibold flex items-center gap-1">
            <Star className="w-3.5 h-3.5 text-[#c9a24a]" /> Mis en avant
          </span>
        </label>
      </div>

      {errors._global && (
        <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600">
          {errors._global}
        </div>
      )}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className={`${press} flex-1 py-3 rounded-xl bg-[#eff2ff] text-[#000a1e] font-semibold text-sm`}
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={saving}
          className={`${press} flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#006b5f] hover:bg-[#005a50] text-white font-semibold text-sm disabled:opacity-60`}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          {saving ? 'Enregistrement...' : isEdit ? 'Enregistrer' : 'Créer le produit'}
        </button>
      </div>
    </form>
  )
}

/* ---------- Ligne produit ---------- */
function ProductRow({ product, onEdit, onDelete, onToggleActive }) {
  return (
    <div className={`${card} p-4 flex items-center gap-4 hover:shadow-md transition-shadow`}>
      {/* Image */}
      <div className="w-16 h-16 rounded-xl bg-[#eff2ff] overflow-hidden shrink-0 flex items-center justify-center">
        {product.image ? (
          <img src={product.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <Package className="w-6 h-6 text-[#006b5f]/40" />
        )}
      </div>

      {/* Infos */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-[#000a1e] truncate">{product.name}</h3>
          {product.is_featured && (
            <span className="px-1.5 py-0.5 bg-[#c9a24a]/15 text-[#c9a24a] text-[10px] font-bold rounded-full flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5 fill-[#c9a24a]" /> TOP
            </span>
          )}
          {!product.is_active && (
            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold rounded-full">
              INACTIF
            </span>
          )}
        </div>
        <p className="text-xs text-[#3c4148] truncate mt-0.5">
          {product.short_description || product.slug}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5">
          {CATEGORIES.find((c) => c.id === product.category)?.label || product.category}
        </p>
      </div>

      {/* Prix */}
      <div className="text-right shrink-0">
        <p className="text-[10px] text-slate-400 uppercase font-semibold">Prix</p>
        <p className="font-bold text-[#000a1e]">{fmt(product.price, product.currency)}</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => onToggleActive(product)}
          className="p-2 rounded-lg hover:bg-[#eff2ff] text-[#3c4148] transition-colors"
          title={product.is_active ? 'Désactiver' : 'Activer'}
        >
          {product.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button
          onClick={() => onEdit(product)}
          className="p-2 rounded-lg hover:bg-[#eff2ff] text-[#006b5f] transition-colors"
          title="Modifier"
        >
          <Edit3 className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(product)}
          className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
          title="Supprimer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

/* ---------- Page principale ---------- */
export default function AdminProducts() {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filterCat, setFilterCat] = useState('')
  const [editing, setEditing] = useState(null) // null = fermé, {} = création, {id,...} = édition
  const [toast, setToast] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const toastTimer = useRef(null)

  const notify = (msg) => {
    setToast(msg)
    clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(''), 2800)
  }
  useEffect(() => () => clearTimeout(toastTimer.current), [])

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const params = {}
      if (search) params.search = search
      if (filterCat) params.category = filterCat
      const { data } = await api.get('/admin/products', { params })
      setProducts(data?.data ?? [])
    } catch (err) {
      console.error(err)
      setError('Impossible de charger les produits.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterCat])

  const handleCreate = () => setEditing({})

  const handleSave = async (payload, id) => {
    if (id) {
      const { data } = await api.put(`/admin/products/${id}`, payload)
      setProducts((ps) => ps.map((p) => (p.id === id ? data.data : p)))
      notify('✅ Produit mis à jour')
    } else {
      const { data } = await api.post('/admin/products', payload)
      setProducts((ps) => [data.data, ...ps])
      notify('✅ Produit créé')
    }
    setEditing(null)
  }

  const handleToggleActive = async (product) => {
    try {
      const { data } = await api.put(`/admin/products/${product.id}`, {
        name: product.name,
        slug: product.slug,
        short_description: product.short_description,
        description: product.description,
        price: product.price,
        currency: product.currency,
        image: product.image,
        category: product.category,
        features: product.features || [],
        is_active: !product.is_active,
        is_featured: product.is_featured,
        position: product.position,
      })
      setProducts((ps) => ps.map((p) => (p.id === product.id ? data.data : p)))
      notify(data.data.is_active ? 'Produit activé' : 'Produit désactivé')
    } catch (err) {
      notify('Erreur lors du changement de statut')
    }
  }

  const handleDelete = (product) => setConfirmDelete(product)

  const doDelete = async () => {
    const product = confirmDelete
    if (!product) return
    try {
      await api.delete(`/admin/products/${product.id}`)
      setProducts((ps) => ps.filter((p) => p.id !== product.id))
      notify('🗑️ Produit supprimé')
    } catch (err) {
      notify('Erreur lors de la suppression')
    } finally {
      setConfirmDelete(null)
    }
  }

  return (
    <div className="min-h-screen bg-[#f9f9ff] p-6 lg:p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#000a1e] flex items-center gap-2">
              <Package className="w-6 h-6 text-[#006b5f]" />
              Gestion des produits
            </h1>
            <p className="text-sm text-[#3c4148] mt-1">
              Ajoutez, modifiez ou supprimez les produits affichés sur la page publique.
            </p>
          </div>
          <button
            onClick={handleCreate}
            className={`${press} flex items-center gap-2 px-4 py-2.5 bg-[#006b5f] hover:bg-[#005a50] text-white rounded-xl font-semibold text-sm shadow-lg shadow-[#006b5f]/20`}
          >
            <Plus className="w-4 h-4" />
            Nouveau produit
          </button>
        </div>

        {/* Barre de recherche + filtre */}
        <div className={`${card} p-3 flex flex-wrap gap-3`}>
          <div className="flex-1 min-w-[220px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un produit..."
              className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f]"
            />
          </div>
          <select
            value={filterCat}
            onChange={(e) => setFilterCat(e.target.value)}
            className="px-3 py-2.5 rounded-xl bg-[#f9f9ff] border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-[#006b5f]/15 focus:border-[#006b5f]"
          >
            <option value="">Toutes catégories</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        {/* Stats rapides */}
        <div className="grid grid-cols-3 gap-4">
          <div className={`${card} p-4`}>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Total</p>
            <p className="text-2xl font-bold text-[#000a1e]">{products.length}</p>
          </div>
          <div className={`${card} p-4`}>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Actifs</p>
            <p className="text-2xl font-bold text-[#006b5f]">
              {products.filter((p) => p.is_active).length}
            </p>
          </div>
          <div className={`${card} p-4`}>
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Mis en avant</p>
            <p className="text-2xl font-bold text-[#c9a24a]">
              {products.filter((p) => p.is_featured).length}
            </p>
          </div>
        </div>

        {/* Liste */}
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#006b5f] animate-spin" />
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 text-center">
            {error}
          </div>
        )}

        {!loading && !error && products.length === 0 && (
          <div className={`${card} p-12 text-center`}>
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="font-bold text-[#000a1e] mb-2">Aucun produit</h2>
            <p className="text-sm text-[#3c4148] mb-6">
              {search || filterCat
                ? 'Aucun résultat pour ce filtre.'
                : 'Créez votre premier produit.'}
            </p>
            {!search && !filterCat && (
              <button
                onClick={handleCreate}
                className={`${press} inline-flex items-center gap-2 px-5 py-2.5 bg-[#006b5f] hover:bg-[#005a50] text-white rounded-xl font-semibold text-sm`}
              >
                <Plus className="w-4 h-4" /> Créer un produit
              </button>
            )}
          </div>
        )}

        {!loading && products.length > 0 && (
          <div className="space-y-3">
            {products.map((p) => (
              <ProductRow
                key={p.id}
                product={p}
                onEdit={setEditing}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal création / édition */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? 'Modifier le produit' : 'Nouveau produit'}
        size="lg"
      >
        {editing && (
          <ProductForm
            product={editing}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      {/* Modal confirmation suppression */}
      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} size="sm">
        {confirmDelete && (
          <div className="text-center space-y-4 pt-2">
            <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h3 className="font-bold text-lg text-[#000a1e]">Supprimer ce produit ?</h3>
            <p className="text-sm text-[#3c4148]">
              <strong>{confirmDelete.name}</strong> sera définitivement supprimé. Cette action
              est irréversible.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className={`${press} flex-1 py-2.5 rounded-xl bg-[#eff2ff] text-[#000a1e] font-semibold text-sm`}
              >
                Annuler
              </button>
              <button
                onClick={doDelete}
                className={`${press} flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white font-semibold text-sm`}
              >
                Supprimer
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[120] bg-[#000a1e] text-white text-sm font-medium px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2">
          <Check className="w-4 h-4 text-[#2dd4bf]" />
          {toast}
        </div>
      )}
    </div>
  )
}